/**
 * The clip a contributor is responding to.
 *
 * Sits beside the title, small and out of the way: it is context for the
 * response, not the main event. Loads nothing until played — a contributor
 * on mobile data should not pay for audio they may not listen to.
 */
import { useEffect, useRef, useState } from "react";
import { formatDuration } from "../lib/validation";
import { IconPause, IconPlay, IconSpeaker } from "./Icons";

interface Props {
  url: string;
  durationSeconds?: number | null;
  /** "record" is the contributor-facing player on the record screen. */
  variant?: "default" | "record";
}

export function ClipPlayer({ url, durationSeconds, variant = "default" }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [failed, setFailed] = useState(false);

  // A new card means a different clip: stop whatever was playing rather than
  // letting the previous one run on underneath the new prompt.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio?.pause();
    };
  }, [url]);

  async function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      // Autoplay policy, a network failure, or an expired URL.
      setFailed(true);
    }
  }

  const total = durationSeconds ?? 0;
  const progress = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;

  const audio = (
    <audio
      ref={audioRef}
      src={url}
      preload="none"
      onPlay={() => {
        setPlaying(true);
        setFailed(false);
      }}
      onPause={() => setPlaying(false)}
      onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
      onEnded={() => {
        setPlaying(false);
        setElapsed(0);
      }}
      onError={() => setFailed(true)}
    />
  );

  if (variant === "record") {
    return (
      <div className={`clip-card ${failed ? "is-failed" : ""}`}>
        <button
          type="button"
          className="clip-card-play"
          onClick={() => void toggle()}
          aria-label={playing ? "Pause the clip" : "Play the clip"}
        >
          {playing ? <IconPause width={14} height={14} /> : <IconPlay width={14} height={14} />}
        </button>
        <div className="clip-card-body">
          <div className="clip-card-track">
            <div className="clip-card-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="clip-card-times">
            <span>{failed ? "Couldn't play this clip" : formatDuration(elapsed)}</span>
            <span>{formatDuration(total)}</span>
          </div>
        </div>
        <IconSpeaker className="clip-card-speaker" width={16} height={16} />
        {audio}
      </div>
    );
  }

  return (
    <div className="clip-player">
      {/* The label heads the panel rather than sitting beside the scrubber,
          so it reads as a title for the clip and not as a caption. */}
      <div className="clip-player-label">
        {failed ? "Couldn't play this clip" : playing ? "Playing the clip" : "Listen to the clip"}
      </div>

      <div className="clip-player-controls">
        <button
          type="button"
          className={`clip-play ${playing ? "is-playing" : ""}`}
          onClick={() => void toggle()}
          aria-label={playing ? "Pause the clip" : "Play the clip"}
        >
          {playing ? "❚❚" : "▶"}
        </button>

        <div className="clip-player-body">
          <div className="clip-progress-track">
            <div className="clip-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <span className="clip-player-time mono">
          {playing || elapsed > 0 ? formatDuration(elapsed) : formatDuration(total)}
        </span>
      </div>

      {audio}
    </div>
  );
}
